import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Search, Plus, Minus, Trash2, ShoppingCart, LogOut,
  UtensilsCrossed, Loader2, Check, Hash, DollarSign,
  ClipboardList, QrCode, HandCoins, RefreshCw, Users,
  ChefHat, Clock, X, CreditCard, Banknote, Smartphone,
  Receipt, AlertCircle
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import type { Tables } from "@/integrations/supabase/types";

type Product = Tables<"products">;
type Category = Tables<"categories">;
type ProductAddon = Tables<"product_addons">;

interface SelectedAddon {
  addon: ProductAddon;
  quantity: number;
}

interface CartItem {
  product: Product;
  quantity: number;
  notes: string;
  addons: SelectedAddon[];
}

const formatCurrency = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

const GarcomPDV = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const [waiter, setWaiter] = useState<any>(null);
  const [restaurant, setRestaurant] = useState<any>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [addonsMap, setAddonsMap] = useState<Record<string, ProductAddon[]>>({});

  // State
  const [activeTab, setActiveTab] = useState("tables");
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedTable, setSelectedTable] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [cartNotes, setCartNotes] = useState("");

  // Addon dialog
  const [addonDialogProduct, setAddonDialogProduct] = useState<Product | null>(null);
  const [addonSelections, setAddonSelections] = useState<Record<string, number>>({});
  const [addonItemNotes, setAddonItemNotes] = useState("");

  // Close tab dialog
  const [closeTabDialog, setCloseTabDialog] = useState<any>(null);
  const [closePaymentMethod, setClosePaymentMethod] = useState<string>("cash");
  const [closingTab, setClosingTab] = useState(false);

  // Dialogs
  const [showCart, setShowCart] = useState(false);
  const [showTipDialog, setShowTipDialog] = useState(false);
  const [tipAmount, setTipAmount] = useState("");
  const [tipTableNumber, setTipTableNumber] = useState<number | null>(null);
  const [processingTip, setProcessingTip] = useState(false);
  const [tipQrCode, setTipQrCode] = useState<string | null>(null);
  const [tipPixCode, setTipPixCode] = useState<string | null>(null);

  // Tables & orders
  const [tables, setTables] = useState<any[]>([]);
  const [kitchenOrders, setKitchenOrders] = useState<any[]>([]);
  const [sendingOrder, setSendingOrder] = useState(false);

  // Load auth + waiter data
  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/garcom/login"); return; }
      setUserId(user.id);

      const { data: w } = await supabase
        .from("waiters")
        .select("*")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .maybeSingle();

      if (!w) { navigate("/garcom/login"); return; }
      setWaiter(w);

      const { data: rest } = await supabase
        .from("restaurants")
        .select("*")
        .eq("id", w.restaurant_id)
        .single();

      setRestaurant(rest);

      // Load products, categories, and addons
      const [prodRes, catRes, addonsRes] = await Promise.all([
        supabase.from("products").select("*").eq("restaurant_id", w.restaurant_id).eq("is_available", true).order("sort_order"),
        supabase.from("categories").select("*").eq("restaurant_id", w.restaurant_id).eq("is_active", true).order("sort_order"),
        supabase.from("product_addons").select("*").eq("is_available", true).order("sort_order"),
      ]);

      setProducts(prodRes.data || []);
      setCategories(catRes.data || []);

      // Build addons map by product_id
      const map: Record<string, ProductAddon[]> = {};
      (addonsRes.data || []).forEach((a: ProductAddon) => {
        if (!map[a.product_id]) map[a.product_id] = [];
        map[a.product_id].push(a);
      });
      setAddonsMap(map);

      setLoading(false);
    };
    init();
  }, [navigate]);

  // Load tables (table_tabs) and kitchen orders
  const loadTables = useCallback(async () => {
    if (!waiter?.restaurant_id) return;

    const { data: tabs } = await supabase
      .from("table_tabs")
      .select("*")
      .eq("restaurant_id", waiter.restaurant_id)
      .in("status", ["open", "awaiting_payment"])
      .order("table_number");

    setTables(tabs || []);

    // Kitchen orders
    const { data: orders } = await supabase
      .from("orders")
      .select("*, order_items(*)")
      .eq("restaurant_id", waiter.restaurant_id)
      .in("status", ["received", "preparing", "ready"])
      .order("created_at", { ascending: false })
      .limit(30);

    setKitchenOrders(orders || []);
  }, [waiter?.restaurant_id]);

  useEffect(() => {
    if (waiter?.restaurant_id) {
      loadTables();
      const interval = setInterval(loadTables, 15000);
      return () => clearInterval(interval);
    }
  }, [waiter?.restaurant_id, loadTables]);

  // Realtime for table_tabs
  useEffect(() => {
    if (!waiter?.restaurant_id) return;
    const channel = supabase
      .channel("waiter-tables")
      .on("postgres_changes", { event: "*", schema: "public", table: "table_tabs", filter: `restaurant_id=eq.${waiter.restaurant_id}` }, () => loadTables())
      .on("postgres_changes", { event: "*", schema: "public", table: "orders", filter: `restaurant_id=eq.${waiter.restaurant_id}` }, () => loadTables())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [waiter?.restaurant_id, loadTables]);

  // Filtered products
  const filteredProducts = useMemo(() => {
    let items = products;
    if (selectedCategory !== "all") items = items.filter(p => p.category_id === selectedCategory);
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(p => p.name.toLowerCase().includes(q));
    }
    return items;
  }, [products, selectedCategory, search]);

  // Handle product click — if has addons show dialog, otherwise add directly
  const handleProductClick = (product: Product) => {
    const productAddons = addonsMap[product.id];
    if (productAddons && productAddons.length > 0) {
      setAddonDialogProduct(product);
      // Pre-fill min_qty defaults
      const defaults: Record<string, number> = {};
      productAddons.forEach(a => {
        defaults[a.id] = 0;
      });
      setAddonSelections(defaults);
      setAddonItemNotes("");
    } else {
      addToCartDirect(product, [], "");
    }
  };

  const addToCartDirect = (product: Product, addons: SelectedAddon[], notes: string) => {
    setCart(prev => [...prev, { product, quantity: 1, notes, addons }]);
  };

  const confirmAddonSelection = () => {
    if (!addonDialogProduct) return;
    const productAddons = addonsMap[addonDialogProduct.id] || [];
    const selected: SelectedAddon[] = [];
    productAddons.forEach(a => {
      const qty = addonSelections[a.id] || 0;
      if (qty > 0) selected.push({ addon: a, quantity: qty });
    });
    addToCartDirect(addonDialogProduct, selected, addonItemNotes);
    setAddonDialogProduct(null);
    toast({ title: `${addonDialogProduct.name} adicionado` });
  };

  const updateQty = (index: number, delta: number) => {
    setCart(prev => prev.map((item, i) => {
      if (i !== index) return item;
      const newQty = item.quantity + delta;
      return newQty <= 0 ? item : { ...item, quantity: newQty };
    }));
  };

  const removeFromCart = (index: number) => setCart(prev => prev.filter((_, i) => i !== index));

  const getItemTotal = (item: CartItem) => {
    const addonTotal = item.addons.reduce((s, a) => s + a.addon.price * a.quantity, 0);
    return (item.product.price + addonTotal) * item.quantity;
  };

  const cartTotal = useMemo(() => cart.reduce((sum, i) => sum + getItemTotal(i), 0), [cart]);
  const serviceFee = restaurant?.service_fee_percentage ? (cartTotal * restaurant.service_fee_percentage / 100) : 0;

  // Open table
  const openTable = async (tableNum: number) => {
    if (!waiter?.restaurant_id) return;
    const existing = tables.find(t => t.table_number === tableNum);
    if (existing) {
      setSelectedTable(tableNum);
      setActiveTab("menu");
      return;
    }

    const { error } = await supabase.from("table_tabs").insert({
      restaurant_id: waiter.restaurant_id,
      table_number: tableNum,
      status: "open",
      total: 0,
    });

    if (error) {
      toast({ title: "Erro ao abrir mesa", description: error.message, variant: "destructive" });
    } else {
      toast({ title: `Mesa ${tableNum} aberta` });
      setSelectedTable(tableNum);
      setActiveTab("menu");
      loadTables();
    }
  };

  // Send order
  const sendOrder = async () => {
    if (!waiter?.restaurant_id || cart.length === 0 || selectedTable === null) return;
    setSendingOrder(true);

    try {
      const subtotal = cartTotal;
      const total = subtotal + serviceFee;

      const { data: order, error: orderErr } = await supabase.from("orders").insert({
        restaurant_id: waiter.restaurant_id,
        order_type: "dine_in" as any,
        status: "received",
        customer_name: customerName || `Mesa ${selectedTable}`,
        table_number: selectedTable,
        subtotal,
        delivery_fee: 0,
        discount: 0,
        total,
        notes: cartNotes || null,
        payment_method: null,
      }).select().single();

      if (orderErr) throw orderErr;

      // Create items with addon info in notes
      const items = cart.map(i => {
        const addonText = i.addons.length > 0
          ? i.addons.map(a => `+ ${a.quantity}x ${a.addon.name} (${formatCurrency(a.addon.price)})`).join("; ")
          : "";
        const fullNotes = [i.notes, addonText].filter(Boolean).join(" | ");
        const addonTotal = i.addons.reduce((s, a) => s + a.addon.price * a.quantity, 0);

        return {
          order_id: order.id,
          product_id: i.product.id,
          product_name: i.product.name,
          quantity: i.quantity,
          unit_price: i.product.price + addonTotal,
          total_price: (i.product.price + addonTotal) * i.quantity,
          notes: fullNotes || null,
        };
      });

      await supabase.from("order_items").insert(items);

      // Update table tab total
      const tab = tables.find(t => t.table_number === selectedTable);
      if (tab) {
        await supabase.from("table_tabs").update({ total: tab.total + total }).eq("id", tab.id);
      }

      toast({ title: "Pedido enviado!", description: `Mesa ${selectedTable} - ${formatCurrency(total)}` });
      setCart([]);
      setCustomerName("");
      setCartNotes("");
      setShowCart(false);
      loadTables();
    } catch (err: any) {
      toast({ title: "Erro ao enviar pedido", description: err.message, variant: "destructive" });
    } finally {
      setSendingOrder(false);
    }
  };

  // Close table tab
  const closeTab = async () => {
    if (!closeTabDialog || !waiter?.restaurant_id) return;
    setClosingTab(true);

    try {
      // Update all open orders for this table to completed
      await supabase.from("orders")
        .update({ status: "completed" as any, payment_method: closePaymentMethod as any, payment_status: "paid" })
        .eq("restaurant_id", waiter.restaurant_id)
        .eq("table_number", closeTabDialog.table_number)
        .in("status", ["received", "preparing", "ready"]);

      // Close the tab
      await supabase.from("table_tabs")
        .update({ status: "closed" })
        .eq("id", closeTabDialog.id);

      toast({ title: `Mesa ${closeTabDialog.table_number} fechada`, description: formatCurrency(closeTabDialog.total) });
      setCloseTabDialog(null);
      loadTables();
    } catch (err: any) {
      toast({ title: "Erro ao fechar comanda", description: err.message, variant: "destructive" });
    } finally {
      setClosingTab(false);
    }
  };

  // Process tip via PIX (Mercado Pago)
  const processTip = async () => {
    if (!tipAmount || parseFloat(tipAmount) <= 0 || !waiter) return;
    setProcessingTip(true);

    try {
      if (waiter.mp_access_token) {
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-waiter-tip`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            waiter_id: waiter.id,
            amount: parseFloat(tipAmount),
            table_number: tipTableNumber,
            restaurant_id: waiter.restaurant_id,
          }),
        });

        const result = await response.json();
        if (!response.ok) throw new Error(result.error || "Erro ao processar gorjeta");

        setTipQrCode(result.qr_code_base64);
        setTipPixCode(result.pix_code);
      } else {
        await supabase.from("waiter_tips").insert({
          waiter_id: waiter.id,
          restaurant_id: waiter.restaurant_id,
          table_number: tipTableNumber,
          amount: parseFloat(tipAmount),
          status: "manual",
        });
        toast({ title: "Gorjeta registrada!", description: formatCurrency(parseFloat(tipAmount)) });
        setShowTipDialog(false);
        setTipAmount("");
      }
    } catch (err: any) {
      toast({ title: "Erro", description: err.message, variant: "destructive" });
    } finally {
      setProcessingTip(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/garcom/login");
  };

  const maxTables = restaurant?.total_tables || 30;
  const tableNumbers = Array.from({ length: maxTables }, (_, i) => i + 1);

  const statusColor: Record<string, string> = {
    received: "bg-blue-500/10 text-blue-600 border-blue-500/30",
    preparing: "bg-amber-500/10 text-amber-600 border-amber-500/30",
    ready: "bg-green-500/10 text-green-600 border-green-500/30",
  };

  const statusLabel: Record<string, string> = {
    received: "Recebido",
    preparing: "Preparando",
    ready: "Pronto",
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-card border-b border-border px-3 py-2 flex items-center justify-between safe-area-inset">
        <div className="flex items-center gap-2 min-w-0">
          <UtensilsCrossed className="w-5 h-5 text-primary shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{waiter?.name}</p>
            <p className="text-[10px] text-muted-foreground truncate">{restaurant?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {cart.length > 0 && (
            <Button variant="outline" size="sm" className="relative" onClick={() => setShowCart(true)}>
              <ShoppingCart className="w-4 h-4" />
              <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{cart.length}</span>
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Tab Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="grid grid-cols-4 mx-2 mt-2 h-10">
          <TabsTrigger value="tables" className="text-xs gap-1"><Hash className="w-3.5 h-3.5" />Mesas</TabsTrigger>
          <TabsTrigger value="menu" className="text-xs gap-1"><ClipboardList className="w-3.5 h-3.5" />Cardápio</TabsTrigger>
          <TabsTrigger value="kitchen" className="text-xs gap-1"><ChefHat className="w-3.5 h-3.5" />Cozinha</TabsTrigger>
          <TabsTrigger value="tips" className="text-xs gap-1"><HandCoins className="w-3.5 h-3.5" />Gorjetas</TabsTrigger>
        </TabsList>

        {/* === TABLES TAB === */}
        <TabsContent value="tables" className="flex-1 p-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold">Mesas do Restaurante</h2>
            <Button variant="ghost" size="sm" onClick={loadTables}><RefreshCw className="w-3.5 h-3.5" /></Button>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
            {tableNumbers.map(num => {
              const tab = tables.find(t => t.table_number === num);
              const isOpen = !!tab;
              return (
                <motion.div key={num} className="relative">
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => openTable(num)}
                    className={`w-full aspect-square rounded-xl flex flex-col items-center justify-center text-sm font-bold border-2 transition-all ${
                      isOpen
                        ? "bg-primary/10 border-primary text-primary"
                        : "bg-muted/30 border-border text-muted-foreground hover:border-primary/50"
                    }`}
                  >
                    <span className="text-lg">{num}</span>
                    {isOpen && (
                      <span className="text-[9px] font-normal mt-0.5">
                        {formatCurrency(tab.total)}
                      </span>
                    )}
                  </motion.button>
                  {isOpen && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setCloseTabDialog(tab); }}
                      className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold shadow-sm hover:scale-110 transition-transform"
                      title="Fechar comanda"
                    >
                      <Receipt className="w-3 h-3" />
                    </button>
                  )}
                </motion.div>
              );
            })}
          </div>
        </TabsContent>

        {/* === MENU TAB === */}
        <TabsContent value="menu" className="flex-1 flex flex-col p-3 gap-3">
          {selectedTable !== null && (
            <Badge variant="secondary" className="self-start gap-1">
              <Hash className="w-3 h-3" /> Mesa {selectedTable}
              <button onClick={() => setSelectedTable(null)} className="ml-1"><X className="w-3 h-3" /></button>
            </Badge>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar produto..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9 text-sm"
            />
          </div>

          {/* Categories */}
          <ScrollArea className="w-full">
            <div className="flex gap-1.5 pb-1">
              <Button
                variant={selectedCategory === "all" ? "default" : "outline"}
                size="sm"
                className="text-xs shrink-0 h-7"
                onClick={() => setSelectedCategory("all")}
              >
                Todos
              </Button>
              {categories.map(c => (
                <Button
                  key={c.id}
                  variant={selectedCategory === c.id ? "default" : "outline"}
                  size="sm"
                  className="text-xs shrink-0 h-7"
                  onClick={() => setSelectedCategory(c.id)}
                >
                  {c.name}
                </Button>
              ))}
            </div>
          </ScrollArea>

          {/* Products */}
          <ScrollArea className="flex-1">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {filteredProducts.map(product => {
                const hasAddons = (addonsMap[product.id]?.length || 0) > 0;
                return (
                  <motion.button
                    key={product.id}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => handleProductClick(product)}
                    className="bg-card border border-border rounded-xl p-3 text-left hover:border-primary/50 transition-all"
                  >
                    {product.image_url && (
                      <img src={product.image_url} alt={product.name} className="w-full h-16 object-cover rounded-lg mb-2" />
                    )}
                    <p className="text-xs font-medium line-clamp-2">{product.name}</p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs font-bold text-primary">{formatCurrency(product.price)}</p>
                      {hasAddons && (
                        <Badge variant="outline" className="text-[9px] px-1 py-0">
                          <Plus className="w-2.5 h-2.5 mr-0.5" />Adicionais
                        </Badge>
                      )}
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </ScrollArea>
        </TabsContent>

        {/* === KITCHEN TAB === */}
        <TabsContent value="kitchen" className="flex-1 p-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold">Pedidos na Cozinha</h2>
            <Button variant="ghost" size="sm" onClick={loadTables}><RefreshCw className="w-3.5 h-3.5" /></Button>
          </div>
          <div className="space-y-2">
            {kitchenOrders.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">Nenhum pedido em preparo</p>
            )}
            {kitchenOrders.map(order => (
              <Card key={order.id} className="border-border/50">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      {order.table_number && (
                        <Badge variant="outline" className="text-[10px]">Mesa {order.table_number}</Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {new Date(order.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    <Badge className={`text-[10px] ${statusColor[order.status] || ""}`}>
                      {statusLabel[order.status] || order.status}
                    </Badge>
                  </div>
                  <div className="space-y-0.5">
                    {order.order_items?.map((item: any) => (
                      <div key={item.id} className="text-xs">
                        <div className="flex justify-between">
                          <span>{item.quantity}x {item.product_name}</span>
                        </div>
                        {item.notes && (
                          <p className="text-[10px] text-muted-foreground ml-4">{item.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* === TIPS TAB === */}
        <TabsContent value="tips" className="flex-1 p-3">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold">Gorjetas</h2>
            <Button size="sm" onClick={() => { setShowTipDialog(true); setTipQrCode(null); setTipPixCode(null); }}>
              <HandCoins className="w-4 h-4 mr-1" /> Solicitar
            </Button>
          </div>

          {!waiter?.mp_access_token && (
            <Card className="border-amber-500/30 bg-amber-500/5 mb-3">
              <CardContent className="p-3">
                <p className="text-xs text-muted-foreground">
                  <strong>Mercado Pago não configurado.</strong> Peça ao administrador para adicionar suas credenciais do Mercado Pago para receber gorjetas via PIX.
                </p>
              </CardContent>
            </Card>
          )}

          <WaiterTipsList waiterId={waiter?.id} />
        </TabsContent>
      </Tabs>

      {/* Floating cart button on mobile */}
      {cart.length > 0 && activeTab === "menu" && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          className="fixed bottom-4 left-3 right-3 z-50"
        >
          <Button
            className="w-full h-12 text-sm font-semibold shadow-lg rounded-xl"
            onClick={() => setShowCart(true)}
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            Ver pedido ({cart.length}) • {formatCurrency(cartTotal + serviceFee)}
          </Button>
        </motion.div>
      )}

      {/* === ADDON SELECTION DIALOG === */}
      <Dialog open={!!addonDialogProduct} onOpenChange={v => { if (!v) setAddonDialogProduct(null); }}>
        <DialogContent className="max-w-sm max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">{addonDialogProduct?.name}</DialogTitle>
            <DialogDescription className="text-xs">
              {formatCurrency(addonDialogProduct?.price || 0)} — selecione os adicionais
            </DialogDescription>
          </DialogHeader>

          {addonDialogProduct && (
            <div className="space-y-3">
              {(addonsMap[addonDialogProduct.id] || []).map(addon => {
                const qty = addonSelections[addon.id] || 0;
                return (
                  <div key={addon.id} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{addon.name}</p>
                      <p className="text-xs text-primary font-semibold">+{formatCurrency(addon.price)}</p>
                      {(addon.min_qty > 0 || addon.max_qty > 1) && (
                        <p className="text-[10px] text-muted-foreground">
                          {addon.min_qty > 0 ? `Mín: ${addon.min_qty}` : ""}{addon.min_qty > 0 && addon.max_qty > 1 ? " • " : ""}{addon.max_qty > 1 ? `Máx: ${addon.max_qty}` : ""}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline" size="icon" className="h-7 w-7"
                        onClick={() => setAddonSelections(prev => ({ ...prev, [addon.id]: Math.max(0, (prev[addon.id] || 0) - 1) }))}
                        disabled={qty === 0}
                      >
                        <Minus className="w-3 h-3" />
                      </Button>
                      <span className="text-sm w-6 text-center font-medium">{qty}</span>
                      <Button
                        variant="outline" size="icon" className="h-7 w-7"
                        onClick={() => setAddonSelections(prev => ({ ...prev, [addon.id]: Math.min(addon.max_qty, (prev[addon.id] || 0) + 1) }))}
                        disabled={qty >= addon.max_qty}
                      >
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}

              <div className="space-y-1">
                <Label className="text-xs">Observações do item</Label>
                <Input
                  value={addonItemNotes}
                  onChange={e => setAddonItemNotes(e.target.value)}
                  placeholder="Ex: sem cebola..."
                  className="h-8 text-sm"
                />
              </div>

              <div className="border-t border-border pt-2">
                {(() => {
                  const addonTotal = Object.entries(addonSelections).reduce((sum, [id, qty]) => {
                    const addon = (addonsMap[addonDialogProduct.id] || []).find(a => a.id === id);
                    return sum + (addon ? addon.price * qty : 0);
                  }, 0);
                  return (
                    <p className="text-sm font-semibold text-right">
                      Total: {formatCurrency((addonDialogProduct.price || 0) + addonTotal)}
                    </p>
                  );
                })()}
              </div>

              <Button className="w-full" onClick={confirmAddonSelection}>
                <Plus className="w-4 h-4 mr-2" /> Adicionar ao pedido
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* === CLOSE TAB DIALOG === */}
      <Dialog open={!!closeTabDialog} onOpenChange={v => { if (!v) setCloseTabDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" /> Fechar Comanda
            </DialogTitle>
            <DialogDescription>
              Mesa {closeTabDialog?.table_number}
            </DialogDescription>
          </DialogHeader>

          {closeTabDialog && (
            <div className="space-y-4">
              <div className="bg-muted/30 rounded-xl p-4 text-center">
                <p className="text-xs text-muted-foreground mb-1">Total da comanda</p>
                <p className="text-2xl font-bold text-primary">{formatCurrency(closeTabDialog.total)}</p>
                {restaurant?.service_fee_percentage > 0 && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Inclui {restaurant.service_fee_percentage}% taxa de serviço
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium">Forma de pagamento</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: "cash", label: "Dinheiro", icon: Banknote },
                    { value: "credit_card", label: "Cartão", icon: CreditCard },
                    { value: "pix", label: "PIX", icon: Smartphone },
                  ].map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      onClick={() => setClosePaymentMethod(value)}
                      className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all text-xs font-medium ${
                        closePaymentMethod === value
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border bg-card text-muted-foreground hover:border-primary/50"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setCloseTabDialog(null)}>
                  Cancelar
                </Button>
                <Button className="flex-1" disabled={closingTab} onClick={closeTab}>
                  {closingTab ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  Fechar conta
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cart Dialog */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Pedido - Mesa {selectedTable}
            </DialogTitle>
          </DialogHeader>

          {selectedTable === null && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              Selecione uma mesa antes de enviar o pedido.
            </div>
          )}

          <div className="space-y-2">
            {cart.map((item, index) => (
              <div key={index} className="p-2 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{item.product.name}</p>
                    <p className="text-xs text-muted-foreground">{formatCurrency(item.product.price)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQty(index, -1)}>
                      <Minus className="w-3 h-3" />
                    </Button>
                    <span className="text-sm w-6 text-center font-medium">{item.quantity}</span>
                    <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => updateQty(index, 1)}>
                      <Plus className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeFromCart(index)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                {/* Show addons */}
                {item.addons.length > 0 && (
                  <div className="ml-2 mt-1 space-y-0.5">
                    {item.addons.map(a => (
                      <p key={a.addon.id} className="text-[10px] text-muted-foreground">
                        + {a.quantity}x {a.addon.name} ({formatCurrency(a.addon.price)})
                      </p>
                    ))}
                  </div>
                )}
                {item.notes && (
                  <p className="text-[10px] text-muted-foreground ml-2 mt-0.5 italic">{item.notes}</p>
                )}
                <p className="text-xs font-semibold text-right mt-1">{formatCurrency(getItemTotal(item))}</p>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <div className="space-y-1">
              <Label className="text-xs">Nome do cliente (opcional)</Label>
              <Input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Cliente" className="h-8 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Observações</Label>
              <Textarea value={cartNotes} onChange={e => setCartNotes(e.target.value)} placeholder="Observações do pedido..." className="text-sm min-h-[60px]" />
            </div>
          </div>

          <div className="border-t border-border pt-2 space-y-1 text-sm">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>{formatCurrency(cartTotal)}</span>
            </div>
            {serviceFee > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>Taxa de serviço ({restaurant?.service_fee_percentage}%)</span>
                <span>{formatCurrency(serviceFee)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-base">
              <span>Total</span>
              <span>{formatCurrency(cartTotal + serviceFee)}</span>
            </div>
          </div>

          <DialogFooter>
            <Button className="w-full" disabled={sendingOrder || selectedTable === null || cart.length === 0} onClick={sendOrder}>
              {sendingOrder ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              Enviar Pedido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tip Dialog */}
      <Dialog open={showTipDialog} onOpenChange={v => { setShowTipDialog(v); if (!v) { setTipQrCode(null); setTipPixCode(null); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HandCoins className="w-5 h-5" /> Gorjeta via PIX
            </DialogTitle>
            <DialogDescription>Gere o QR Code PIX para receber gorjeta</DialogDescription>
          </DialogHeader>

          {tipQrCode ? (
            <div className="text-center space-y-3">
              <img src={`data:image/png;base64,${tipQrCode}`} alt="QR Code PIX" className="w-48 h-48 mx-auto rounded-lg border" />
              {tipPixCode && (
                <div className="space-y-1">
                  <Label className="text-xs">Código Copia e Cola</Label>
                  <Input value={tipPixCode} readOnly className="text-xs" onClick={e => { (e.target as HTMLInputElement).select(); navigator.clipboard.writeText(tipPixCode); toast({ title: "Código copiado!" }); }} />
                </div>
              )}
              <p className="text-xs text-muted-foreground">Aguardando pagamento...</p>
              <Button variant="outline" onClick={() => { setShowTipDialog(false); setTipQrCode(null); setTipPixCode(null); setTipAmount(""); }}>
                Fechar
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Mesa (opcional)</Label>
                <Input
                  type="number"
                  placeholder="Nº da mesa"
                  value={tipTableNumber || ""}
                  onChange={e => setTipTableNumber(e.target.value ? parseInt(e.target.value) : null)}
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label>Valor da gorjeta</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="1"
                  placeholder="R$ 0,00"
                  value={tipAmount}
                  onChange={e => setTipAmount(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="flex gap-2">
                {[5, 10, 20, 50].map(v => (
                  <Button key={v} variant="outline" size="sm" className="flex-1 text-xs" onClick={() => setTipAmount(String(v))}>
                    R${v}
                  </Button>
                ))}
              </div>
              <Button
                className="w-full"
                disabled={!tipAmount || parseFloat(tipAmount) <= 0 || processingTip}
                onClick={processTip}
              >
                {processingTip ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <QrCode className="w-4 h-4 mr-2" />}
                {waiter?.mp_access_token ? "Gerar QR Code PIX" : "Registrar Gorjeta"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Sub-component for tip history
const WaiterTipsList = ({ waiterId }: { waiterId?: string }) => {
  const [tips, setTips] = useState<any[]>([]);

  useEffect(() => {
    if (!waiterId) return;
    const load = async () => {
      const { data } = await supabase
        .from("waiter_tips")
        .select("*")
        .eq("waiter_id", waiterId)
        .order("created_at", { ascending: false })
        .limit(50);
      setTips(data || []);
    };
    load();

    const channel = supabase
      .channel("waiter-tips-" + waiterId)
      .on("postgres_changes", { event: "*", schema: "public", table: "waiter_tips", filter: `waiter_id=eq.${waiterId}` }, () => load())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [waiterId]);

  const totalToday = tips
    .filter(t => new Date(t.created_at).toDateString() === new Date().toDateString())
    .reduce((s, t) => s + (t.amount || 0), 0);

  return (
    <div className="space-y-3">
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-3 text-center">
          <p className="text-xs text-muted-foreground">Gorjetas hoje</p>
          <p className="text-xl font-bold text-primary">{formatCurrency(totalToday)}</p>
        </CardContent>
      </Card>

      {tips.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">Nenhuma gorjeta registrada</p>
      )}
      {tips.map(tip => (
        <div key={tip.id} className="flex items-center justify-between p-2 bg-muted/20 rounded-lg text-sm">
          <div>
            {tip.table_number && <span className="text-xs text-muted-foreground">Mesa {tip.table_number} • </span>}
            <span className="text-xs text-muted-foreground">
              {new Date(tip.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Badge variant={tip.status === "paid" ? "default" : "secondary"} className="text-[10px]">
              {tip.status === "paid" ? "Pago" : tip.status === "manual" ? "Manual" : "Pendente"}
            </Badge>
            <span className="font-semibold">{formatCurrency(tip.amount)}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default GarcomPDV;
